// Video Director start / end frame slots shared by the Add shot and Insert shot panels.
// Empty slot = dashed "+ Add …" card; click → source menu (anchored, keyboard accessible); filled slot =
// the image + source label + Change / Remove. Presentational: the panels own the state and the request bodies.

import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { LuArrowRight, LuChevronDown, LuPlus, LuX } from "react-icons/lu";
import { RefImage, effectiveModel, modelIds } from "./DirectorPanels";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);

/** Action-row button: one 34 px box for primary / ghost alike. */
export const actClass = (kind) =>
  `add-video-modal__btn add-video-modal__btn--${kind} add-video-modal__fs-act`;

/** Anchored popover list: ↑ ↓ move, Enter picks, Esc / outside click closes (Esc never reaches the panel). */
export function SourceMenu({ label, sources, onClose }) {
  const ref = useRef(null);
  const items = () => [...ref.current.querySelectorAll("[role=menuitem]:not(:disabled)")];
  useEffect(() => {
    items()[0]?.focus();
    const away = (e) => !ref.current?.parentElement?.contains(e.target) && onClose();
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const list = items();
      const at = list.indexOf(document.activeElement);
      list[(at + (e.key === "ArrowDown" ? 1 : -1) + list.length) % list.length]?.focus();
    }
  };
  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      className="add-video-modal__fs-menu"
      onKeyDown={onKeyDown}
    >
      {sources.map(({ id, label: text, Icon, hint, disabled, onSelect }) => (
        <button
          key={id}
          type="button"
          role="menuitem"
          disabled={disabled}
          className="add-video-modal__fs-menu-item"
          onClick={() => {
            onClose();
            onSelect();
          }}
        >
          {Icon && <Icon size={14} />}
          <span>{text}</span>
          {hint && <span className="add-video-modal__fs-menu-hint">{hint}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * One frame slot. `visual` (a node) makes it filled; `sources` is the menu of the empty / Change state.
 * `footer` replaces the default Change / Remove row (Insert's fixed start frame); `extra` adds buttons to it.
 */
export function FrameSlot({
  name,
  label,
  addLabel,
  hint,
  disabled,
  visual,
  sourceLabel,
  note,
  sources,
  onRemove,
  extra,
  footer,
}) {
  const [menu, setMenu] = useState(false);
  const filled = !!visual;
  const trigger = useRef(null);
  const closeMenu = () => {
    setMenu(false);
    trigger.current?.focus();
  };
  return (
    <div className="add-video-modal__fs-slot" role="group" aria-label={label} data-slot={name}>
      <div className="add-video-modal__fs-label">{label}</div>
      <div className="add-video-modal__fs-anchor">
        {filled ? (
          <div className="add-video-modal__fs-card add-video-modal__fs-card--filled">{visual}</div>
        ) : (
          <button
            ref={trigger}
            type="button"
            className="add-video-modal__fs-card add-video-modal__fs-card--empty"
            disabled={disabled}
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => setMenu(true)}
          >
            <LuPlus size={18} />
            <span className="add-video-modal__fs-add">{addLabel}</span>
            <span className="add-video-modal__fs-hint">{hint}</span>
          </button>
        )}
        {menu && <SourceMenu label={t("ChooseSource")} sources={sources} onClose={closeMenu} />}
      </div>
      <div className="add-video-modal__fs-foot">
        {footer || (
          <>
            <span className="add-video-modal__fs-src" title={sourceLabel}>
              {filled ? sourceLabel : ""}
              {filled && note && <em>{note}</em>}
            </span>
            {filled && (
              <>
                {extra}
                <button
                  ref={filled ? trigger : undefined}
                  type="button"
                  className="add-video-modal__fs-link"
                  aria-haspopup="menu"
                  aria-expanded={menu}
                  onClick={() => setMenu(true)}
                >
                  {t("FrameChange")}
                </button>
                {onRemove && (
                  <button type="button" className="add-video-modal__fs-link" onClick={onRemove}>
                    {t("FrameRemove")}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Stands in for the end slot of a model that takes no last frame. */
export function NoEndFrame() {
  return (
    <div
      className="add-video-modal__fs-slot"
      data-slot="end"
      style={{ fontSize: 12, color: "#64748b" }}
    >
      {t("NoEndFrame")}
    </div>
  );
}

/** Two equal slots with an arrow, or — while one picks from a gallery — the inline picker in their place. */
export function SlotRow({ start, end, picker }) {
  return (
    <div className="add-video-modal__fs-row">
      {picker || (
        <>
          {start}
          <LuArrowRight size={18} className="add-video-modal__fs-arrow" aria-hidden="true" />
          {end}
        </>
      )}
    </div>
  );
}

/** Inline picker shell: title + Done, scrolls inside its own box. */
export function SlotPicker({ title, onDone, children }) {
  return (
    <div className="add-video-modal__fs-picker" role="group" aria-label={title}>
      <div className="add-video-modal__fs-picker-head">
        <span>{title}</span>
        <button type="button" className="add-video-modal__fs-link" onClick={onDone}>
          <LuX size={12} /> {t("PickerDone")}
        </button>
      </div>
      <div className="add-video-modal__fs-picker-body">{children}</div>
    </div>
  );
}

const readyUrl = (item) => (item?.status === "ready" && item.url ? item.url : null);

/**
 * Tiles (≥96 px, 🔍 corner preview, click selects). entries = [{ key, item, label }]; isOn(entry); onPick(entry).
 */
export function RefGrid({ entries, isOn, onPick }) {
  if (entries.length === 0)
    return <div style={{ fontSize: 12, color: "#64748b" }}>{t("NothingHere")}</div>;
  const ready = entries.filter((e) => readyUrl(e.item));
  const items = ready.map((e) => ({ url: e.item.url, name: e.label, description: e.item.prompt }));
  return (
    <div className="add-video-modal__fs-grid">
      {entries.map((e) => {
        const on = isOn(e);
        return (
          <button
            key={e.key}
            type="button"
            aria-pressed={on}
            aria-label={e.label}
            disabled={e.item.status === "failed"}
            className={`add-video-modal__director-ref add-video-modal__director-ref--pick${on ? " add-video-modal__director-ref--on" : ""}`}
            onClick={() => onPick(e)}
          >
            <RefImage item={e.item} preview={{ items, index: ready.indexOf(e), click: false }} />
          </button>
        );
      })}
    </div>
  );
}

/** Compact "Omni · 53 cr" select (one control instead of a pill group). Null price = disabled. */
export function ModelSelect({ kind, value, onChange, models }) {
  const current = effectiveModel(models, kind, value);
  return (
    <span className="add-video-modal__fs-select">
      <select
        className="add-video-modal__fs-act"
        aria-label={t(`Models.${kind}`)}
        value={current}
        onChange={(e) => onChange(e.target.value)}
      >
        {modelIds(models, kind).map((id) => {
          const price = models?.[kind]?.[id];
          return (
            <option key={id} value={id} disabled={price === null}>
              {t(`Models.${id}`)}
              {typeof price === "number" ? ` · ${t("Models.PriceShort", { n: price })}` : ""}
            </option>
          );
        })}
      </select>
      <LuChevronDown size={14} aria-hidden="true" />
    </span>
  );
}
